import { useTranslation } from "react-i18next";
import styles from "./ZeitonLoader.module.scss";

// The two halves of the ZEITON sign, on the design's 120px grid.
const LEFT = "M79.6 0 H32 A32 32 0 0 0 0 32 V88 A32 32 0 0 0 32 120 H36.4 Z";
const RIGHT = "M83.6 0 H88 A32 32 0 0 1 120 32 V88 A32 32 0 0 1 88 120 H40.4 Z";

interface ZeitonLoaderProps {
    /** Width and height in px. The design draws it at 120. */
    size?: number;
    /** "Loading your workspace" under the sign: the full-screen loader shows it. */
    caption?: boolean;
    className?: string;
}

/** The sign in one of its two colourings: the bright tone on the left, or swapped. */
function Sign({ size, swapped, className }: { size: number; swapped: boolean; className: string }) {
    return (
        <svg className={className} width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true" focusable="false">
            <path d={LEFT} transform="translate(0 -2)" className={swapped ? styles.dark : styles.bright} />
            <path d={RIGHT} transform="translate(0 5)" className={swapped ? styles.bright : styles.dark} />
        </svg>
    );
}

/**
 * The app's loading indicator, the design's "L7 · Swap": nothing moves, the bright and the dark
 * tone of the ZEITON sign swap halves and swap back.
 *
 * The swap is the sign drawn twice, the swapped one on top fading in and out. Only its opacity is
 * animated, which the browser runs off the main thread: the loader keeps going while the page is
 * busy rendering the data it waited for. (Animating the fill colour stalled with the page.)
 */
export function ZeitonLoader({ size = 56, caption = false, className }: ZeitonLoaderProps) {
    const { t } = useTranslation();
    return (
        <div className={[styles.loader, className].filter(Boolean).join(" ")} role="status" aria-label={t("Loading")!}>
            <span className={styles.mark} style={{ width: size, height: size }}>
                <Sign size={size} swapped={false} className={styles.base} />
                <span className={styles.swap}><Sign size={size} swapped className={styles.layer} /></span>
            </span>
            {caption ? <span className={styles.caption}>{t("Loading your workspace")}</span> : null}
        </div>
    );
}
